// Filtered combobox over membership() selectable names (GRANT (15)).
// Not a native <select>. Not a <datalist> over free text — a datalist still
// submits "Marketng". Submit is only a value in the selectable set. Exact
// match of the filter text against the set is a confirm (keeps tests that
// set .account-input.value then click edit). List filtering is case-insensitive;
// submit (`getValue` / Enter) is case-sensitive (`Set.has` on the ledger name).
import { amVisibleDisplayText } from "../../ingest-worker/src/am_visible_text.js";
import { clear, h } from "./dom.js";
import type { ChartLoadState } from "./chart_load.js";
import type { StaffRole } from "./types.js";
import { svgIcon } from "./ui/components.js";
import { advice } from "./ui/capabilities.js";
import { accountLabel, accountLabelSet, type AccountLabelContext } from "./ui/display-words.js";
import * as face from "./ui/face.js";
import { readerRole } from "./ui/facts.js";
import { navLabel } from "./ui/sentences.js";

/**
 * Wallet types that can pair with a bank account.
 * Lockstep with pipeline/moov_wallet.py BANK_PAIRABLE_TYPES.
 */
export const BANK_PAIRABLE_MOOV_TYPES = ["payout", "top-up", "cash-out"] as const;

const LIKELY_BANK_SEGMENTS = new Set([
  "Cash",
  "Bank",
  "Checking",
  "Savings",
  "Operating",
]);

const lastChoiceByKey = new Map<string, string>();

function memoryKey(kind: "account" | "location", groupKey: string): string {
  return `${kind}\n${groupKey}`;
}

export function resetChartChoiceMemory(): void {
  lastChoiceByKey.clear();
}

/**
 * The visible name for an account path. Stored and submitted values stay the
 * full colon name; the display drops a company root segment when one is in
 * front of a standard ledger root and joins the remaining path with ` › `.
 * The words themselves come from `accountLabel` (rd Y0): an account is
 * written one way everywhere.
 */
export function accountDisplayName(
  account: string,
  companies: AccountLabelContext,
): string {
  if (account.trim() === "") return "";
  return accountLabel(account, companies);
}

export function rememberChartChoice(
  kind: "account" | "location",
  groupKey: string,
  value: string,
): void {
  if (groupKey.length === 0 || value.length === 0) return;
  lastChoiceByKey.set(memoryKey(kind, groupKey), value);
}

export function lastChartChoice(
  kind: "account" | "location",
  groupKey: string,
): string | undefined {
  return lastChoiceByKey.get(memoryKey(kind, groupKey));
}

/**
 * Pairable Moov descriptor token, or null.
 * "moov payout" / "payout" / "topup" / "cash-out". A tenant payment is not pairable.
 */
export function moovPairableType(descriptor: string | undefined): string | null {
  const cleaned = (descriptor ?? "").toLowerCase().replace(/\s+/g, " ").trim();
  const raw = cleaned.startsWith("moov ") ? cleaned.slice(5).trim() : cleaned;
  const token = raw.split(" ")[0] ?? "";
  if (token === "topup") return "top-up";
  if (token === "cashout") return "cash-out";
  if (
    token === "payout" ||
    token === "top-up" ||
    token === "cash-out"
  ) {
    return token;
  }
  return null;
}

/**
 * Likely counter-account for a pairable Moov movement.
 * Assets: at a segment in {Cash, Bank, Checking, Savings, Operating} at depth
 * 3 or more, and never a path that contains the segment Moov (the wallet).
 * Assets:SYN:Chase:Operating matches. Assets:SYN:Moov:Operating does not.
 * Assets:*:Bank* would miss real books (the bank name is not the segment "Bank").
 */
export function isLikelyBankCounterAccount(account: string): boolean {
  const parts = account.split(":").filter((part) => part.length > 0);
  if (parts[0] !== "Assets") return false;
  if (parts.includes("Moov")) return false;
  for (let index = 2; index < parts.length; index += 1) {
    if (LIKELY_BANK_SEGMENTS.has(parts[index] ?? "")) return true;
  }
  return false;
}

/** Snapshot is absent *and* the chooser has nothing to offer. */
export function chartLoadIsGenuinelyUnloaded(load: ChartLoadState): boolean {
  if (load.status === "error") return false;
  return (
    load.status === "absent" &&
    load.snapshot.selectableAccounts.length === 0 &&
    load.snapshot.selectableLocations.length === 0
  );
}

/**
 * What to say when the name a person typed is not on the list.
 *
 * The owner is the person this console is built for, so the owner is never
 * told to "escalate to the owner" or to ask themselves. The account manager
 * keeps the ask. With no role the historical wording is kept, because the
 * queue card (not this lane's file) still asks for it that way.
 */
export function requestSlotCopy(
  kind: "account" | "location",
  load: ChartLoadState,
  role?: StaffRole,
): string {
  // Owner decision 10: an unknown role is the account manager (least access).
  const reader = readerRole(role);
  // An action nobody can do in the console reads decision 2's sentence for
  // both roles: opening an account or adding a place (rd-r12 §2b decisions 2
  // and 10).
  const addOne = advice(kind === "account" ? "open_account" : "add_place", reader);
  if (load.status === "error") {
    // The account manager can retry a failed list; that is not the owner's
    // action (rc-5 §2).
    return `The ${kind} list did not load. Try again from the top of the page.`;
  }
  const names =
    kind === "account"
      ? load.snapshot.selectableAccounts
      : load.snapshot.selectableLocations;
  if (names.length > 0) {
    if (kind === "account") {
      // A miss is not a dead end: pick a listed account, or add the missing
      // one in the ledger by hand (decision 2).
      return `No account has that name. Try another word. ${addOne} Pick a listed account instead.`;
    }
    return `No location has that name. Try another word. ${addOne}`;
  }
  if (load.status === "absent") {
    // The account manager can open the books (rc-5 §2); both roles read the
    // same advice because both can.
    return `The ${kind} list is not loaded yet. ${advice("open_books", reader)} Then return here to choose one.`;
  }
  return kind === "account"
    ? `The list has no account names yet. ${addOne}`
    : `The list has no location names yet. ${addOne}`;
}

function emptyListCopy(
  kind: "account" | "location",
  load: ChartLoadState | undefined,
  selectableCount: number,
): string {
  if (load?.status === "error") return "The chart could not be loaded";
  if (selectableCount > 0) {
    return kind === "account" ? "No account matches" : "No location matches";
  }
  if (load?.status === "absent") return "The chart has not been loaded yet";
  return kind === "account"
    ? "The chart lists no account names"
    : "The chart lists no location names";
}

export interface ChartChooser {
  root: HTMLElement;
  filter: HTMLInputElement;
  /**
   * Hidden mirror of the stored name (the full colon path for an account).
   * `filter` shows the readable label after a choice; this input is what a
   * form would submit. `getValue()` stays the authority: the mirror is
   * refreshed on every choice and input event.
   */
  valueInput: HTMLInputElement;
  /** Value the control would submit — null if the filter is not in the set. */
  getValue: () => string | null;
  /** How many names the current filter offers. Zero means "no match". */
  matchCount: () => number;
  /** How many names the list holds at all, filter aside. */
  optionCount: () => number;
}

export interface MountChartChooserOptions {
  kind: "account" | "location";
  selectable: readonly string[];
  className: string;
  ariaLabel: string;
  placeholder: string;
  /** Prefill only when this name is in selectable. */
  initialValue?: string;
  /** Load outcome — empty-list copy must not invent a request mechanism. */
  load?: ChartLoadState;
  /** Names drawn in a "Likely" group. Ignored unless they are also selectable. */
  likely?: readonly string[];
  /**
   * Two NAMED groups instead of the Likely/All pair (rd16 U, C3; orchestrator
   * decision 2026-11-03). The list is painted `primary` first under
   * `primaryLabel`, then every other name under `restLabel`; the filter, the
   * count and `getValue` still cover every selectable name, so a name in the
   * second group is as choosable as one in the first.
   *
   * A caller uses this when one set is the likely answer but the others are
   * legitimate: a statement arrives for a bank or a card, and an account the
   * page cannot recognise must still be pickable or a real bank account becomes
   * impossible to upload a statement for.
   */
  groups?: ChartChooserGroups;
  /**
   * When set, an empty initial value preloads this group's last exact choice,
   * and a later exact choice is remembered for the next card in the group.
   */
  groupKey?: string;
  /**
   * The words for a list that is not a chart list (the Split editor's company
   * picker, rd r12 F1 R5). Without it the list speaks of the chart's accounts
   * or locations, which is wrong for a list of companies: an empty company
   * picker read "The chart lists no location names".
   */
  listWords?: ChooserListWords;
  /**
   * The page's ONE account-label context (`face.accountLabelContext`): ledger
   * company segment -> the company's plain name. Required (rd13 L1): a picker
   * is where two look-alike accounts are chosen between, so it is the last
   * place that may print an internal key. A location list ignores it.
   */
  companies: AccountLabelContext;
}

/** A non-chart list's own nouns and empty sentences. */
export interface ChooserListWords {
  /** Plural noun: "companies" (the toggle and the "All …" group). */
  many: string;
  /** The list holds names, but none matches what was typed. */
  noMatch: string;
  /** The list holds no names at all. */
  empty: string;
}

/**
 * Two named groups for a list where one set is the likely answer and the rest
 * are still legitimate choices (rd16 U, C3). Both labels are read by a person:
 * plain English, no ids, no count.
 */
export interface ChartChooserGroups {
  /** The heading of the likely set, e.g. "Bank and card accounts". */
  primaryLabel: string;
  /** The names under that heading. Names not in `selectable` are ignored. */
  primary: readonly string[];
  /** The heading of every other selectable name, e.g. "Other accounts". */
  restLabel: string;
}

/**
 * The count line above the list, or null when there is nothing true to say.
 * It is a count of what the company's chart lists, so it needs a chart that
 * loaded: a failed read (or a chart that was never pushed) lists nothing
 * because we could not read it, and "0" would claim the company has none
 * (re-check H5). A caller that does not say how the read went gets a count only
 * when it holds names, since an empty list cannot be told from a failed read.
 *
 * rd16 U, C3: the number is the number of choices the list is OFFERING right
 * now, so it moves as a person types. It used to print the whole chart's count
 * while the list below it showed two matches — a count that did not describe
 * what was on screen.
 */
function countLabel(
  kind: "account" | "location",
  count: number,
  load: ChartLoadState | undefined,
): string | null {
  if (load !== undefined && load.status !== "ok") return null;
  if (load === undefined && count === 0) return null;
  const noun = kind === "account" ? "account" : "location";
  const counted = count === 1 ? `1 ${noun}` : `${String(count)} ${noun}s`;
  return kind === "account"
    ? `Accounts you can choose from: ${String(count)}`
    : counted;
}

export function mountChartChooser(opts: MountChartChooserOptions): ChartChooser {
  // Chart order is preserved: it is the tie-break the ranking below promises
  // ("ties keep chart order"), and the chart the owner pushed is already in
  // the order the books read (round 10, C-7).
  const selectable = [...new Set(opts.selectable)];
  const selectableSet = new Set(selectable);
  const likelyNames = [
    ...new Set((opts.likely ?? []).filter((name) => selectableSet.has(name))),
  ].sort();
  const likelySet = new Set(likelyNames);
  /**
   * The first band's names for a two-group caller (rd16 U, C3). Order comes from
   * the caller, and a name it lists that is not selectable is ignored — the
   * list can only ever offer what the chart holds.
   */
  const primarySet = new Set(
    (opts.groups?.primary ?? []).filter((name) => selectableSet.has(name)),
  );
  const remembered =
    opts.groupKey !== undefined && opts.groupKey.length > 0
      ? lastChartChoice(opts.kind, opts.groupKey)
      : undefined;
  const seeded =
    opts.initialValue !== undefined && selectableSet.has(opts.initialValue)
      ? opts.initialValue
      : remembered !== undefined && selectableSet.has(remembered)
        ? remembered
        : "";

  // What the field, the list and the "chosen" line show for a stored name.
  // An account is shown in words (rd M3: no colon path on screen), and where
  // two offered accounts would read the same the label names what tells them
  // apart (re-check E6). A location is a plain name, never run through the
  // account rules ("DeKalb Office" is not "De kalb office"), but it is
  // staff-entered text, so the person reads the cleaned form (re-check E3).
  // The stored name rides in `valueInput` and is what `getValue` returns.
  const accountLabels =
    opts.kind === "account"
      ? accountLabelSet(selectable, opts.companies)
      : new Map<string, string>();
  const shownFor = (name: string): string =>
    opts.kind === "account"
      ? (accountLabels.get(name) ?? accountDisplayName(name, opts.companies))
      : amVisibleDisplayText(name);
  const filter = h("input", {
    type: "text",
    class: opts.className,
    placeholder: opts.placeholder,
    value: seeded.length > 0 ? shownFor(seeded) : "",
    role: "combobox",
    "aria-label": opts.ariaLabel,
    "aria-autocomplete": "list",
    "aria-expanded": "false",
    autocomplete: "off",
  });
  const valueInput = h("input", {
    type: "hidden",
    "data-role": "chart-chooser-value",
    value: seeded,
  });
  const listId = `chart-chooser-${opts.kind}-${String(Math.random()).slice(2)}`;
  filter.setAttribute("aria-controls", listId);

  const list = h("ul", {
    class: "chart-chooser-list",
    id: listId,
    role: "listbox",
    hidden: "true",
  });

  const chevron = h(
    "button",
    {
      type: "button",
      class: "chart-chooser-chevron",
      "data-role": "chart-chooser-toggle",
      "aria-label":
        opts.listWords !== undefined
          ? `Show ${opts.listWords.many}`
          : opts.kind === "account"
            ? "Show accounts"
            : "Show locations",
    },
    [svgIcon("chevron")],
  );

  const words = opts.listWords;
  // A non-chart list (the Split editor's company picker) prints no count: the
  // count line is a count of the chart's names, and "2 locations" over a list
  // of companies would be wrong (rd r12 F1, R5).
  const countText = countLabel(opts.kind, selectable.length, opts.load);
  const shownCount = words === undefined ? countText : null;
  const count = h(
    "p",
    {
      class: "chart-chooser-count",
      "data-role": "chart-chooser-count",
      ...(shownCount === null ? { hidden: "true" } : {}),
    },
    [shownCount ?? ""],
  );
  /**
   * Keep the count equal to what the list is offering (rd16 U, C3). Only the
   * chart-derived count line is repainted: a non-chart list (the Split editor's
   * company picker) prints no count at all. It is a client-side filter of the
   * chart this page already loaded, and it is computed with the load state so a
   * failed read still prints no number.
   */
  const paintCount = (shown: number): void => {
    if (words !== undefined) return;
    const text = countLabel(opts.kind, shown, opts.load);
    count.hidden = text === null;
    if (text === null) {
      count.setAttribute("hidden", "true");
      count.textContent = "";
      return;
    }
    count.removeAttribute("hidden");
    count.textContent = text;
  };
  const chosen = h(
    "p",
    {
      class: "chart-chooser-chosen",
      "data-role": "chart-chooser-chosen",
      hidden: "true",
    },
    [],
  );

  let highlight = -1;
  let chosenName: string | null =
    seeded.length > 0 && selectableSet.has(seeded) ? seeded : null;

  // The field holds the choice when it reads as the stored name or as the
  // words shown for it.
  const showsChoice = (name: string): boolean => {
    const typed = filter.value.trim();
    return typed === name || typed === shownFor(name);
  };

  const filtered = (): string[] => {
    // After a choice the field holds the full stored name. Reopening must
    // still offer every account (the chosen one preselected), not only the
    // one account the substring filter would otherwise match.
    if (chosenName !== null && showsChoice(chosenName)) {
      return selectable;
    }
    const exact = filter.value.trim();
    // A stored name typed exactly is the value the form submits; keep offering
    // it. This is not a hidden-segment search: a partial query never reaches
    // into the path.
    const exactMatch = selectableSet.has(exact) ? [exact] : [];
    const q = exact.toLowerCase();
    if (q.length === 0) return selectable;
    // ONLY the words a person can see match (round 10, C-7): a company code or
    // any other hidden path segment (`Assets:RENT:Due-From:TRST`) never brings
    // an account into the list. A label that starts with the query ranks above
    // one that only contains it; ties keep chart order.
    const matches = selectable.filter((name) =>
      shownFor(name).toLowerCase().includes(q),
    );
    const starts = matches.filter((name) => shownFor(name).toLowerCase().startsWith(q));
    const ordered = [...starts, ...matches.filter((name) => !starts.includes(name))];
    return [...exactMatch, ...ordered.filter((name) => name !== exact)];
  };

  // A readable name may be typed instead of the stored one, but only when it
  // names exactly one account; a shared display name is ambiguous, so it is
  // not an exact match and submits nothing.
  const byDisplayName = (typed: string): string | null => {
    const matches = selectable.filter((name) => shownFor(name) === typed);
    return matches.length === 1 ? (matches[0] ?? null) : null;
  };

  const currentChoice = (): string | null =>
    chosenName !== null && showsChoice(chosenName) ? chosenName : null;

  // The submit value. A made choice wins, so two accounts that share a label
  // still submit the one that was picked; otherwise the typed text must be a
  // listed name, or a readable name that fits exactly one account.
  const valueNow = (): string | null => {
    const choice = currentChoice();
    if (choice !== null) return choice;
    const typed = filter.value.trim();
    if (selectableSet.has(typed)) return typed;
    return byDisplayName(typed);
  };

  const syncValue = (): void => {
    valueInput.value = valueNow() ?? "";
  };

  // Put a choice in the field: the words in view, the stored name beside it.
  const setChoice = (name: string): void => {
    filter.value = shownFor(name);
    chosenName = name;
    syncValue();
  };

  const orderedOf = (names: string[]): string[] => {
    // One display order, used by the list painter, the highlight line and the
    // keyboard walk, so a highlighted row is the row the person sees
    // (rd16 U, C3).
    const out: string[] = [];
    for (const group of groupsOf(names)) out.push(...group.names);
    return out;
  };

  const paintChosen = (): void => {
    const typed = filter.value.trim();
    const value = selectableSet.has(typed) ? typed : byDisplayName(typed);
    clear(chosen);
    if (value === null) {
      chosen.hidden = true;
      chosen.setAttribute("hidden", "true");
      return;
    }
    // The field already shows the words after a choice; the line below is for
    // a typed stored name, to say what it means.
    if (opts.kind === "account" && typed === shownFor(value)) {
      chosen.hidden = true;
      chosen.setAttribute("hidden", "true");
      return;
    }
    chosen.hidden = false;
    chosen.removeAttribute("hidden");
    chosen.append(document.createTextNode(shownFor(value)));
  };

  const remember = (name: string): void => {
    if (opts.groupKey !== undefined && opts.groupKey.length > 0) {
      rememberChartChoice(opts.kind, opts.groupKey, name);
    }
  };

  /**
   * The list's groups, in display order (rd16 U, C3).
   *
   * A `groups` caller gets two named bands, and the second band is every name
   * the first does not hold — so a name the page could not classify is still
   * on the list. Without `groups` the older Likely/All pair stands (the
   * category chooser and the Split editor rely on it). A band is dropped when
   * it would be empty, and `label === null` is the single unlabelled band.
   */
  const groupsOf = (names: string[]): Array<{ label: string | null; names: string[] }> => {
    if (opts.groups !== undefined) {
      const primary = names.filter((name) => primarySet.has(name));
      const rest = names.filter((name) => !primarySet.has(name));
      const bands: Array<{ label: string | null; names: string[] }> = [];
      if (primary.length > 0) {
        bands.push({ label: opts.groups.primaryLabel, names: primary });
      }
      if (rest.length > 0) bands.push({ label: opts.groups.restLabel, names: rest });
      return bands.length > 0 ? bands : [{ label: null, names }];
    }
    const likelyVisible = names.filter((name) => likelySet.has(name));
    if (likelyVisible.length === 0) return [{ label: null, names }];
    const rest = names.filter((name) => !likelySet.has(name));
    const bands: Array<{ label: string | null; names: string[] }> = [
      { label: "Likely", names: likelyVisible },
    ];
    if (rest.length > 0) {
      bands.push({
        label:
          words !== undefined
            ? `All ${words.many}`
            : opts.kind === "account"
              ? "All accounts"
              : "All locations",
        names: rest,
      });
    }
    return bands;
  };

  const paintList = (): void => {
    const names = filtered();
    paintCount(names.length);
    while (list.firstChild) list.removeChild(list.firstChild);
    let optionIndex = 0;
    const appendOption = (name: string): void => {
      const index = optionIndex;
      optionIndex += 1;
      const option = h(
        "li",
        {
          class:
            index === highlight
              ? "chart-chooser-option chart-chooser-option-active"
              : "chart-chooser-option",
          role: "option",
          "aria-selected": name === currentChoice() ? "true" : "false",
          "data-name": name,
        },
        [shownFor(name)],
      );
      option.addEventListener("mousedown", (event) => {
        event.preventDefault();
        setChoice(name);
        remember(name);
        highlight = -1;
        paintChosen();
        closeList();
        filter.dispatchEvent(new Event("change", { bubbles: true }));
      });
      list.append(option);
    };
    for (const band of groupsOf(names)) {
      if (band.label !== null) {
        list.append(
          h("li", { class: "chart-chooser-group", role: "presentation" }, [band.label]),
        );
      }
      for (const name of band.names) appendOption(name);
    }
    if (names.length === 0) {
      list.append(
        h("li", { class: "chart-chooser-empty", role: "presentation" }, [
          words !== undefined
            ? selectable.length > 0
              ? words.noMatch
              : words.empty
            : emptyListCopy(opts.kind, opts.load, selectable.length),
        ]),
      );
    }
  };

  const openList = (): void => {
    const choice = currentChoice();
    highlight = choice === null ? -1 : orderedOf(filtered()).indexOf(choice);
    paintList();
    list.removeAttribute("hidden");
    filter.setAttribute("aria-expanded", "true");
  };

  const closeList = (): void => {
    list.setAttribute("hidden", "true");
    filter.setAttribute("aria-expanded", "false");
    highlight = -1;
  };

  filter.addEventListener("focus", () => {
    openList();
  });
  filter.addEventListener("click", () => {
    openList();
  });
  filter.addEventListener("input", () => {
    chosenName = null;
    syncValue();
    paintChosen();
    openList();
  });
  filter.addEventListener("blur", () => {
    window.setTimeout(() => {
      closeList();
    }, 0);
  });
  filter.addEventListener("keydown", (event) => {
    const names = filtered();
    const ordered = orderedOf(names);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (list.hasAttribute("hidden")) openList();
      highlight = Math.min(highlight + 1, ordered.length - 1);
      paintList();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      highlight = Math.max(highlight - 1, 0);
      paintList();
      return;
    }
    if (event.key === "Enter") {
      const highlighted = highlight >= 0 ? ordered[highlight] : undefined;
      const typed = filter.value.trim();
      const pick =
        highlighted !== undefined
          ? highlighted
          : selectableSet.has(typed)
            ? typed
            : null;
      if (pick !== null) {
        event.preventDefault();
        setChoice(pick);
        remember(pick);
        paintChosen();
        closeList();
        filter.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }
    if (event.key === "Escape") {
      closeList();
    }
  });

  chevron.addEventListener("mousedown", (event) => {
    event.preventDefault();
  });
  chevron.addEventListener("click", () => {
    if (list.hasAttribute("hidden")) openList();
    else closeList();
  });

  paintChosen();
  syncValue();

  const row = h("div", { class: "chart-chooser-row" }, [filter, chevron]);
  const root = h("div", { class: "chart-chooser", "data-role": "chart-chooser" }, [
    row,
    valueInput,
    count,
    chosen,
    list,
  ]);

  return {
    root,
    filter,
    valueInput,
    getValue: () => {
      const value = valueNow();
      valueInput.value = value ?? "";
      return value;
    },
    matchCount: () => filtered().length,
    optionCount: () => selectable.length,
  };
}

/**
 * Exactly one location: show the name as text and always submit it.
 * The input keeps .location-input so callers read the same value.
 */
export function mountSingleLocation(opts: {
  name: string;
  className: string;
  ariaLabel: string;
}): ChartChooser {
  // The location name is staff-entered API text: the person reads the cleaned
  // form (rule 20), while the hidden value and getValue() keep the stored name
  // so the booking is posted against the real location.
  const shown = amVisibleDisplayText(opts.name);
  const filter = h("input", {
    type: "text",
    class: opts.className,
    value: shown,
    readonly: "true",
    tabindex: "-1",
    "aria-label": opts.ariaLabel,
    "data-role": "location-single-input",
  });
  const root = h(
    "div",
    {
      class: "chart-chooser location-fixed",
      "data-role": "chart-chooser",
      "data-location-mode": "single",
    },
    [
      h("p", { class: "location-fixed-text", "data-role": "location-fixed-text" }, [
        `Property: ${shown}`,
      ]),
      filter,
    ],
  );
  const valueInput = h("input", {
    type: "hidden",
    "data-role": "chart-chooser-value",
    value: opts.name,
  });
  root.append(valueInput);
  return {
    root,
    filter,
    valueInput,
    getValue: () => opts.name,
    matchCount: () => 1,
    optionCount: () => 1,
  };
}

export interface RequestSlotOptions {
  /** Whose words. Omit for the historical wording (the queue card's). */
  role?: StaffRole;
  /**
   * When given, the line stays out of the layout until the search finds no
   * match — it is an answer to a search, not a permanent fixture.
   */
  chooser?: ChartChooser;
  /**
   * Where a link to Rules is available, the owner's no-match line carries it
   * (round 10, B9-M3). Omit where there is no navigation.
   */
  onOpenRules?: () => void;
}

const SLOT_ROLE: Record<"account" | "location", string> = {
  account: "request-new-account",
  location: "request-new-location",
};

export function requestSlot(
  kind: "account" | "location",
  load: ChartLoadState,
  options: RequestSlotOptions = {},
): HTMLElement {
  const copy = requestSlotCopy(kind, load, options.role);
  // A polite status region, like `recurring-live` and `upload-progress-label`:
  // a search that finds nothing must answer the person who typed, including a
  // screen-reader user. It stays in the document while quiet, because a region
  // that is `hidden` when its text changes is never announced; an empty one is
  // collapsed by CSS (`.chart-request-slot:empty`).
  const slot = h("p", {
    class: "muted chart-request-slot",
    "data-role": SLOT_ROLE[kind],
    role: "status",
    "aria-live": "polite",
  });
  const text = h("span", { "data-role": "request-slot-text" });
  slot.append(text);
  const rulesLink = (): HTMLElement | null => {
    if (options.onOpenRules === undefined || options.role !== "owner" || kind !== "account") {
      return null;
    }
    const rulesWords = `Open ${navLabel("rules")}`;
    const link = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "open-rules-from-chooser",
        "aria-label": face.ariaFor(rulesWords),
      },
      [rulesWords],
    );
    link.addEventListener("click", () => options.onOpenRules?.());
    return link;
  };
  const paintLink = (shown: boolean): void => {
    const existing = slot.querySelector('[data-action="open-rules-from-chooser"]');
    if (!shown) {
      existing?.remove();
      return;
    }
    if (existing === null) {
      const link = rulesLink();
      if (link !== null) slot.append(link);
    }
  };
  const chooser = options.chooser;
  if (chooser === undefined) {
    text.textContent = copy;
    paintLink(copy.length > 0);
    return slot;
  }
  const paint = (): void => {
    // An unsearchable list (a failed or absent chart) or an empty one has
    // nothing to match, so the line is the only thing to say.
    const searchable = load.status !== "error" && chooser.optionCount() > 0;
    const noMatch =
      chooser.filter.value.trim().length > 0 && chooser.matchCount() === 0;
    const next = !searchable || noMatch ? copy : "";
    if (text.textContent !== next) text.textContent = next;
    paintLink(next.length > 0);
  };
  chooser.filter.addEventListener("input", paint);
  chooser.filter.addEventListener("change", paint);
  paint();
  return slot;
}

export function requestNewAccountSlot(load: ChartLoadState): HTMLElement {
  return requestSlot("account", load);
}

export function requestNewLocationSlot(load: ChartLoadState): HTMLElement {
  return requestSlot("location", load);
}
